import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';
import useGroupContext from '../../../contexts/group/useGroupContext';
import useOperatorContext from '../../../contexts/operator/useOperatorContext';
import ScrollToTop from '../../../hooks/scrollToTop';
import { getDisplayName } from '../../../services/channel';
import PageSpinnerThree from '../../general/PageSpinnerThree';
import AppLayout from '../../layouts/app/AppLayout';
import {
    ContainerHeading,
    HeroImage,
    StyledContainer,
    StyledFulfilment,
    StyledFulfilmentContainer,
    StyledHeaderRow,
    StyledOrderDetails,
    StyledRow,
} from '../Confirmation/ConfirmationPage';

// What a guest sees once the lead has placed the group order: that it's gone through, when it's arriving
// and what they added. It's laid out like the lead's confirmation page, without the customer, payment,
// price or basket details.
const GroupConfirmationPage = () => {
    const groupContext = useGroupContext();
    const operatorContext = useOperatorContext();
    const navigate = useNavigate();

    const confirmation = groupContext.state?.confirmation;

    // nothing to confirm if it didn't go through, or this isn't somebody's group order
    useEffect(() => {
        if (!groupContext.session || groupContext.ended === 'cancelled') navigate('/');
    }, [groupContext.session, groupContext.ended]);

    if (!confirmation) {
        return <PageSpinnerThree show={true} />;
    }

    const displayName = operatorContext.operator?.theme.v4.catering_label ?? getDisplayName('catering');

    return (
        <AppLayout page="confirmation">
            <ScrollToTop />
            <Helmet>
                <title>{operatorContext.operator?.name} - {t`Order confirmation - Powered by Orderswift`}</title>
            </Helmet>
            <StyledContainer>
                <StyledHeaderRow>
                    <h1 className="page-header">
                        {t`Thank you`}{groupContext.name ? <>, {groupContext.name}!</> : '!'}
                    </h1>
                </StyledHeaderRow>
                <StyledRow>
                    <StyledFulfilmentContainer>
                        <StyledFulfilment>
                            <ContainerHeading first={true}>
                                {t`Confirmed`} <span className="material-symbols-outlined material-symbols-outlined--filled">check_circle</span>
                            </ContainerHeading>
                            <div className="block">
                                <div className="operator-site">{operatorContext.operator?.name} &middot; {confirmation.site_name}</div>
                                <div className="order-ref">
                                    {groupContext.host
                                        ? t`${groupContext.host} placed the order and paid for it, so there's nothing more for you to do.`
                                        : t`The host placed the order and paid for it, so there's nothing more for you to do.`}
                                </div>
                            </div>
                            <ContainerHeading>{displayName}</ContainerHeading>
                            <div className={`block block--end ${operatorContext.operator?.theme.v4.hero_2 ? '' : 'block--end--no-hero'}`}>
                                <div className="address">
                                    {t`Delivery`}{confirmation.address ? <> &middot; {confirmation.address}</> : null}
                                </div>
                                {confirmation.timeslot && <div className="timeslot">{confirmation.timeslot}</div>}
                            </div>
                        </StyledFulfilment>
                        {operatorContext.operator?.theme.v4.hero_2 && <HeroImage />}
                    </StyledFulfilmentContainer>
                    <StyledOrderDetails>
                        <h2 className="conf__heading conf__heading--order">{t`Your items`}</h2>
                        <div className="block block--end">
                            {groupContext.lines.length === 0 ? (
                                <Empty>{t`You didn't add anything to this order.`}</Empty>
                            ) : (
                                <Items>
                                    {groupContext.lines.map((line) => (
                                        <li key={line.line_id}>
                                            <span className="quantity">{line.order.quantity}</span>
                                            <span className="details">
                                                <span className="name">{line.display.name}</span>
                                                {line.display.modifiers.some((mod) => mod.quantity !== 0) && (
                                                    <span className="mods">
                                                        {line.display.modifiers
                                                            .filter((mod) => mod.quantity !== 0)
                                                            .map((mod) =>
                                                                mod.quantity < 0
                                                                    ? t`No ${mod.name}`
                                                                    : mod.quantity > 1 ? `${mod.name} (${mod.quantity})` : mod.name
                                                            )
                                                            .join(', ')}
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </Items>
                            )}
                        </div>
                    </StyledOrderDetails>
                </StyledRow>
            </StyledContainer>
        </AppLayout>
    );
};

const Items = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    color: var(--text-black);

    li {
        display: flex;
        gap: 14px;
        padding-bottom: 12px;
        font-size: 16px;
        line-height: 24px;
    }

    li:last-child {
        padding-bottom: 0;
    }

    .quantity {
        flex-shrink: 0;
        min-width: 24px;
        text-align: center;
    }

    .details {
        display: flex;
        flex-direction: column;
    }

    .mods {
        font-size: 14px;
        line-height: 20px;
        color: var(--text-grey);
    }
`;

const Empty = styled.p`
    margin: 0;
    color: var(--text-grey);
`;

export default GroupConfirmationPage;
